import React from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import {
  useSubmissionScoreEvent,
  type ScoreEventDimension,
  type ScoreEventRisk,
} from '@/lib/supabase/submissions';
import { DIMENSION_LABELS } from '@/lib/scoring/methodology';
import {
  labelColor,
  scoreToLabel,
  capitalizeLabel,
} from '@/lib/scoring/score-display';

// ── Band → display label (mirrors _shared/scoring/presentation.ts) ────────────

const DEAL_READINESS_LABEL: Record<string, string> = {
  'not-ready': 'Not Ready',
  developing: 'Developing',
  'nearly-ready': 'Nearly Ready',
  ready: 'Ready',
};

const EVIDENCE_GRADE_LABEL: Record<string, string> = {
  high: 'High',
  moderate: 'Moderate',
  low: 'Low',
};

const EVIDENCE_LEVEL_LABEL: Record<string, string> = {
  verified: 'Verified',
  'partially-verified': 'Partial',
  unverified: 'Unverified',
};

const SEVERITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

function severityColor(severity: string, colors: any): string {
  switch (severity) {
    case 'high':
      return colors.error ?? '#ef4444';
    case 'medium':
      return colors.warning ?? '#f59e0b';
    default:
      return colors.success ?? '#22c55e';
  }
}

function titleCase(id: string): string {
  return id
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

interface SubmissionScoreDetailProps {
  startupProfileId: string;
  /** Headline score from the submission row — shown immediately while the event loads. */
  fallbackScore: number | null;
  /** Mean per-dimension confidence from the submission row (composite carries no confidence). */
  confidence: number | null;
}

/**
 * The rich IRS breakdown for a scored submission, fetched from the canonical
 * score_event: headline score + venture label, evidence grade · confidence,
 * deal-readiness band, the granular risk list (category · severity — description),
 * and the per-dimension scores. Mirrors the founder-side score view so investors
 * see the same depth instead of a bare number.
 */
export function SubmissionScoreDetail({
  startupProfileId,
  fallbackScore,
  confidence,
}: SubmissionScoreDetailProps) {
  const colors = useThemeColors();
  const { data: event, isLoading, isError } = useSubmissionScoreEvent(startupProfileId);

  const headlineScore = event?.composite.finalScore ?? fallbackScore ?? 0;
  const ventureLabel = event?.composite.ventureLabel ?? scoreToLabel(headlineScore);
  // Band colour is canonical in both states — ventureLabel already falls back to
  // scoreToLabel(headlineScore) before the event loads.
  const headlineColor = labelColor(ventureLabel);

  const sortedRisks: ScoreEventRisk[] = event
    ? [...event.risks].sort(
        (a, b) => (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9),
      )
    : [];

  const sortedDims: ScoreEventDimension[] = event
    ? [...event.dimensions].sort((a, b) => b.rawScore - a.rawScore)
    : [];

  return (
    <View
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: colors.surface.secondary,
        borderWidth: 1,
        borderColor: colors.border.light,
      }}
    >
      {/* Headline: score + venture label + confidence */}
      <View className="flex-row items-center px-5 py-4">
        <View className="flex-row items-baseline">
          <Text style={{ color: headlineColor }} className="text-4xl font-bold">
            {headlineScore}
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-sm ml-0.5">
            /100
          </Text>
        </View>
        <View
          className="ml-3 px-2.5 py-1 rounded-full"
          style={{ backgroundColor: `${headlineColor}1A` }}
        >
          <Text style={{ color: headlineColor }} className="text-xs font-bold" numberOfLines={1}>
            {capitalizeLabel(ventureLabel)}
          </Text>
        </View>
        <View className="flex-1" />
        {confidence != null && (
          <View className="items-end">
            <Text style={{ color: colors.text.primary }} className="text-base font-bold">
              {confidence}%
            </Text>
            <Text style={{ color: colors.text.tertiary }} className="text-[10px]">
              Confidence
            </Text>
          </View>
        )}
      </View>

      {isLoading && (
        <View className="px-5 pb-5 pt-1">
          <ActivityIndicator color={colors.primary[600]} />
        </View>
      )}

      {isError && (
        <View
          className="mx-4 mb-4 px-4 py-3 rounded-xl"
          style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
        >
          <Text style={{ color: colors.text.secondary }} className="text-sm">
            Couldn&apos;t load the full breakdown. The headline score is shown above.
          </Text>
        </View>
      )}

      {event && (
        <View
          className="px-5 pb-5"
          style={{ borderTopWidth: 1, borderTopColor: colors.border.light, paddingTop: 12 }}
        >
          {/* Composite rows */}
          <DefRow
            label="Evidence"
            value={`${EVIDENCE_GRADE_LABEL[event.composite.evidenceGrade] ?? capitalizeLabel(event.composite.evidenceGrade)}${
              confidence != null ? ` · ${confidence}%` : ''
            }`}
            colors={colors}
          />
          <DefRow
            label="Deal Readiness"
            value={DEAL_READINESS_LABEL[event.composite.dealReadiness] ?? capitalizeLabel(event.composite.dealReadiness)}
            colors={colors}
          />
          <DefRow
            label="Venture Quality"
            value={`${Math.round(event.composite.fourAxis.ventureQuality)} / 100`}
            colors={colors}
          />

          {/* Granular risks — category · severity — description */}
          <SectionHeading text="Key Risks" colors={colors} />
          {sortedRisks.length === 0 ? (
            <Text style={{ color: colors.text.tertiary }} className="text-sm py-1">
              None flagged
            </Text>
          ) : (
            sortedRisks.map((r, i) => (
              <View key={`risk-${i}`} className="py-1.5">
                <View className="flex-row items-center flex-wrap">
                  <Text style={{ color: colors.text.primary }} className="text-sm font-semibold">
                    {titleCase(r.category)}
                  </Text>
                  <View
                    className="ml-2 px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${severityColor(r.severity, colors)}1A` }}
                  >
                    <Text
                      style={{ color: severityColor(r.severity, colors) }}
                      className="text-[10px] font-bold uppercase"
                    >
                      {r.severity}
                    </Text>
                  </View>
                </View>
                {!!r.description && (
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-sm mt-0.5 leading-5"
                    numberOfLines={3}
                  >
                    {r.description}
                  </Text>
                )}
              </View>
            ))
          )}

          {/* Per-dimension breakdown */}
          <SectionHeading text="Dimension Breakdown" colors={colors} />
          <View className={isWeb ? 'flex-row flex-wrap -mx-1' : undefined}>
            {sortedDims.map((d) => (
              <View
                key={`dim-${d.id}`}
                className={isWeb ? 'px-1 mb-2' : 'py-1.5'}
                style={isWeb ? { width: '50%' } : undefined}
              >
                <View className="flex-row items-center">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-sm font-medium flex-1"
                    numberOfLines={1}
                  >
                    {DIMENSION_LABELS[d.id as keyof typeof DIMENSION_LABELS] ?? titleCase(d.id)}
                  </Text>
                  <Text
                    style={{ color: labelColor(scoreToLabel(d.rawScore)) }}
                    className="text-sm font-bold ml-2"
                  >
                    {d.rawScore}
                  </Text>
                </View>
                <Text style={{ color: colors.text.tertiary }} className="text-[11px] mt-0.5">
                  {EVIDENCE_LEVEL_LABEL[d.evidenceLevel] ?? d.evidenceLevel} · {d.confidence}% confidence
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

function DefRow({ label, value, colors }: { label: string; value: string; colors: any }) {
  return (
    <View className="flex-row items-center py-1.5">
      <Text style={{ color: colors.text.secondary }} className="text-sm flex-1" numberOfLines={1}>
        {label}
      </Text>
      <Text style={{ color: colors.text.primary }} className="text-sm font-semibold" numberOfLines={1}>
        {value}
      </Text>
    </View>
  );
}

function SectionHeading({ text, colors }: { text: string; colors: any }) {
  return (
    <Text
      style={{ color: colors.text.tertiary }}
      className="text-[10px] font-bold uppercase tracking-wider mt-4 mb-1"
    >
      {text}
    </Text>
  );
}
